import React, { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import { LogHourEntry } from "../../../constants/types";
import "./style.scss";
import * as Yup from "yup";
import { useFormik } from "formik";

interface LogHourEntryModalProps {
  show: boolean;
  onHide: () => void;
  data?: LogHourEntry | null;
  onSave: (entry: LogHourEntry) => void;
}

// Yup schema for modal
const validationSchema = Yup.object({
  hours: Yup.number()
    .typeError("Hours must be a number")
    .min(0, "Hours cannot be less than 0")
    .max(24, "Hours cannot be more than 24")
    .test(
      "hours-or-minutes",
      "Either hours or minutes must be greater than 0",
      function (value) {
        const { minutes } = this.parent;
        return (value ?? 0) > 0 || (minutes ?? 0) > 0;
      }
    ),
  minutes: Yup.number()
    .typeError("Minutes must be a number")
    .min(0, "Minutes cannot be less than 0")
    .max(60, "Minutes cannot be more than 60")
    .test(
      "hours-or-minutes",
      "Either hours or minutes must be greater than 0",
      function (value) {
        const { hours } = this.parent;
        return (value ?? 0) > 0 || (hours ?? 0) > 0;
      }
    ),
  notes: Yup.string().max(500, "Notes cannot exceed 500 characters"),
});

const LogHourEntryModal: React.FC<LogHourEntryModalProps> = ({ show, onHide, data, onSave }) => {

  const formik = useFormik({
    initialValues: {
      hours: "",
      minutes: "",
      notes: "",
    },
    validationSchema,
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: (values) => {
      const entry: LogHourEntry = {
        hours: Number(values.hours) || 0,
        minutes: Number(values.minutes) || 0,
        notes: values.notes || "",
        uuid: data?.uuid || "",
      };
      onSave(entry);
      onHide();
    },
  });

  useEffect(() => {
    if (!show) {
      formik.resetForm();
    }
  }, [show]);

  useEffect(() => {
    if (data && show) {
      formik.setValues({
        hours: data.hours?.toString() || "",
        minutes: data.minutes?.toString() || "",
        notes: data.notes || "",
      });
    }
  }, [data, show]);



  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
    >
      <Modal.Header closeButton className="Mileage-Entry-header">
        <Modal.Title>{data ? "Update Log Hours" : "Add Log Hours"}</Modal.Title>
      </Modal.Header>

      <Modal.Body className="Mileage-Entry-Body">
        <form onSubmit={formik.handleSubmit}>
          <div className="row g-3 mb-3">
            <div className="col-12 col-sm-12 col-md-12 col-lg-6">
              <label className="form-label">Hours</label>
              <input
                type="number"
                name="hours"
                className="form-control commonInput bg-light"
                placeholder="Enter Hours"
                min={0}
                max={24}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.hours}
                required
              />
              {formik.touched.hours && formik.errors.hours && (
                <div className="text-danger">{formik.errors.hours}</div>
              )}
            </div>

            <div className="col-12 col-sm-12 col-md-12 col-lg-6">
              <label className="form-label d-none d-md-block">Minutes</label>
              <input
                type="number"
                name="minutes"
                className="form-control commonInput bg-light"
                placeholder="Enter Minutes"
                min={0}
                max={60}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.minutes}
                required
              />
              {formik.touched.minutes && formik.errors.minutes && (
                <div className="text-danger">{formik.errors.minutes}</div>
              )}
            </div>

            <div className="col-12 col-sm-12 col-md-12 col-lg-12">
              <div className="mb-4">
                <label htmlFor="taskDescription" className="form-label ">
                  Add Noted
                </label>
                <textarea
                  id="taskDescription"
                  name="notes"
                  className="form-control commonInput bg-light"
                  rows={4}
                  maxLength={500}
                  placeholder="Enter Notes..."
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.notes}
                />
                <div className="text-end text-muted small mt-1">
                  {formik.values.notes.length}/500 Characters
                </div>
                {formik.touched.notes && formik.errors.notes && (
                  <div className="text-danger">{formik.errors.notes}</div>
                )}
              </div>
            </div>
          </div>

          <div className="modal-submit-button">
            <button
              type="submit"
              className="btn btn-warning"
              disabled={formik.isSubmitting || !formik.isValid}
            >
              {data ? "Update" : "Save"}
            </button>
          </div>
        </form>
      </Modal.Body>
    </Modal>
  );
};

export default LogHourEntryModal; 